import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./context/AuthContext";
import { startSyncLoop } from "./lib/driverSync";
import { ProtectedRoute } from "./components/layout/ProtectedRoute";
import { DriverProtectedRoute } from "./components/layout/DriverProtectedRoute";
import { CustomerProtectedRoute } from "./components/layout/CustomerProtectedRoute";
import { PosProtectedRoute } from "./components/layout/PosProtectedRoute";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { SectionStateGuard } from "./components/layout/SectionStateGuard";
import { Toaster } from "./components/ui/sonner";
import { useRealtime } from "./lib/useRealtime";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Subscriptions from "./pages/Subscriptions";
import SubscriptionDetail from "./pages/SubscriptionDetail";
import Deliveries from "./pages/Deliveries";
import Drivers from "./pages/Drivers";
import DriverDetail from "./pages/DriverDetail";
import DriverColors from "./pages/DriverColors";
import RoutesPage from "./pages/Routes";
import RouteDetail from "./pages/RouteDetail";
import RouteBuilder from "./pages/RouteBuilder";
import RouteStickers from "./pages/RouteStickers";
import RoutePacking from "./pages/RoutePacking";
import RoutePackingPrint from "./pages/RoutePackingPrint";
import LiveMap from "./pages/LiveMap";
import Payments from "./pages/Payments";
import PayLink from "./pages/PayLink";
import DriverRoutes from "./pages/driver/DriverRoutes";
import DriverRouteDetail from "./pages/driver/DriverRouteDetail";
import DriverStopAction from "./pages/driver/DriverStopAction";
import CustomerRegister from "./pages/customer/CustomerRegister";
import CustomerHome from "./pages/customer/CustomerHome";
import CustomerPayments from "./pages/customer/CustomerPayments";
import CustomerDeliveryHistory from "./pages/customer/CustomerDeliveryHistory";
import CustomerUpdates from "./pages/customer/CustomerUpdates";
import CustomerQrWallet from "./pages/customer/CustomerQrWallet";
import CustomerProfile from "./pages/customer/CustomerProfile";
import CustomerSetPassword from "./pages/customer/CustomerSetPassword";
import AdminManagement from "./pages/AdminManagement";
import { RequirePermission } from "./components/layout/RequirePermission";
import PosScanner from "./pages/pos/PosScanner";
import Settings from "./pages/Settings";
import Catalog from "./pages/Catalog";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import Support from "./pages/Support";
import NotFound from "./pages/NotFound";

// Bridges server-sent change events into TanStack Query cache invalidations for
// live multi-admin sync. Lives inside AuthProvider (needs the current user).
function RealtimeBridge() {
  useRealtime();
  return null;
}

export default function App() {
  // Drive the offline delivery-sync retry loop app-wide (not tied to any one
  // screen) so queued deliveries + photos keep uploading in the background
  // across navigation and reconnects, until the server confirms.
  React.useEffect(() => startSyncLoop(), []);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <RealtimeBridge />
      <BrowserRouter>
        <SectionStateGuard />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/pay/:token" element={<PayLink />} />
          <Route path="/Support" element={<Support />} />
          <Route path="/support" element={<Support />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <Customers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <CustomerDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <ProtectedRoute>
                <Subscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscriptions/:id"
            element={
              <ProtectedRoute>
                <SubscriptionDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deliveries"
            element={
              <ProtectedRoute>
                <Deliveries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers"
            element={
              <ProtectedRoute>
                <Drivers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers/colors"
            element={
              <ProtectedRoute>
                <DriverColors />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers/:id"
            element={
              <ProtectedRoute>
                <DriverDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routes"
            element={
              <ProtectedRoute>
                <RoutesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routes/builder"
            element={
              <ProtectedRoute>
                <RouteBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routes/:id"
            element={
              <ProtectedRoute>
                <RouteDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routes/:id/stickers"
            element={
              <ProtectedRoute>
                <RouteStickers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routes/:id/packing"
            element={
              <ProtectedRoute>
                <RoutePacking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routes/:id/packing/print"
            element={
              <ProtectedRoute>
                <RoutePackingPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-map"
            element={
              <ProtectedRoute>
                <LiveMap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <Payments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-management"
            element={
              <ProtectedRoute>
                <RequirePermission superOnly>
                  <AdminManagement />
                </RequirePermission>
              </ProtectedRoute>
            }
          />
          <Route path="/driver/login" element={<Login />} />
          <Route
            path="/driver"
            element={
              <DriverProtectedRoute>
                <DriverRoutes />
              </DriverProtectedRoute>
            }
          />
          <Route
            path="/driver/routes/:routeId"
            element={
              <DriverProtectedRoute>
                <DriverRouteDetail />
              </DriverProtectedRoute>
            }
          />
          <Route
            path="/driver/routes/:routeId/stops/:stopId"
            element={
              <DriverProtectedRoute>
                <DriverStopAction />
              </DriverProtectedRoute>
            }
          />
          <Route path="/customer/login" element={<Login />} />
          <Route path="/customer/register" element={<CustomerRegister />} />
          <Route path="/customer/set-password" element={<CustomerSetPassword />} />
          <Route
            path="/customer"
            element={
              <CustomerProtectedRoute>
                <CustomerHome />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/payments"
            element={
              <CustomerProtectedRoute>
                <CustomerPayments />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/history"
            element={
              <CustomerProtectedRoute>
                <CustomerDeliveryHistory />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/updates"
            element={
              <CustomerProtectedRoute>
                <CustomerUpdates />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/qr"
            element={
              <CustomerProtectedRoute>
                <CustomerQrWallet />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/profile"
            element={
              <CustomerProtectedRoute>
                <CustomerProfile />
              </CustomerProtectedRoute>
            }
          />
          <Route path="/pos/login" element={<Login />} />
          <Route
            path="/pos"
            element={
              <PosProtectedRoute>
                <PosScanner />
              </PosProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/catalog"
            element={
              <ProtectedRoute>
                <Catalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFound />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
      </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
